import { createMemo, Show } from "solid-js";
import type { StreamingChatId, StreamingOperationId } from "@storytell/streaming";
import { OperationCreateAction } from "./internal/MockOperationCreateAction";
import { useWire } from "~/wire";
import { TbHelpCircleFilled } from "solid-icons/tb";

interface TurnCompleteRendererProps {
  block: { operationId: StreamingOperationId };
  chatId: StreamingChatId;
}

/**
 * Renders a "virtual" block that shows completion details for a conversation.
 * This is not a real block that is generated by the server.
 */
export const TurnCompleteRenderer = (props: TurnCompleteRendererProps) => {
  const wire = useWire();

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  };

  const formatTime = (date?: Date) => {
    if (!date) return "";
    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Find the message that contains this operation
  const getMessage = () => {
    const operation = parentOperation();
    if (!operation) return null;

    const messageId = operation.messageId;
    if (!messageId) return null;

    return wire.stream.message.get(messageId);
  };

  // Get credits and token information from the specific message
  const getConversationMetrics = () => {
    const message = getMessage();
    const metrics = message?.aggregateMetrics;

    return {
      totalCredits: metrics?.totalCredits || 0,
      totalTokens: (metrics?.ingressTokens || 0) + (metrics?.egressTokens || 0),
      ingressTokens: metrics?.ingressTokens || 0,
      egressTokens: metrics?.egressTokens || 0,
    };
  };

  // Check if we have any metric data to show
  const hasMetrics = () => {
    const metrics = getConversationMetrics();
    return metrics.totalCredits > 0 || metrics.totalTokens > 0;
  };

  // Find the parent operation for this block to get completion timestamp
  const parentOperation = () => wire.stream.operation.get(props.block.operationId);

  // Get completion timestamp and processing time from the operation
  const operationCompletedAt = createMemo(() => {
    return parentOperation()?.completedAt;
  });

  const operationProcessingTime = createMemo(() => {
    const op = parentOperation();
    if (op?.completedAt && op.startedAt) {
      return op.completedAt.getTime() - op.startedAt.getTime();
    }
    return undefined;
  });

  return (
    <div class="pl-4 flex flex-col gap-2 min-w-0 w-full" data-renderer="conversation-complete">
      <div class="flex items-start gap-4">
        <div class="text-xs text-gray-500 dark:text-gray-500 flex items-start gap-3 flex-wrap">
          <Show when={operationCompletedAt()}>
            <span>Completed {formatTime(operationCompletedAt())}</span>
          </Show>
          <Show when={operationProcessingTime()}>
            <span>{formatDuration(operationProcessingTime()!)}</span>
          </Show>
          {/* Credit consumption */}
          <Show when={hasMetrics() && getConversationMetrics() && getConversationMetrics().totalCredits > 0}>
            <div class="flex items-center gap-2">
              <span>
                <span class="font-bold">{getConversationMetrics().totalCredits.toLocaleString("en-US")}</span> total
                credits used
              </span>
              <a
                class="inline text-brand text-xs"
                href="https://go.storytell.ai/learn-credits"
                target="_blank"
                rel="noreferrer"
              >
                <TbHelpCircleFilled class="size-4" />
              </a>
            </div>
          </Show>
        </div>
      </div>
      <div class="self-end">
        <Show when={hasMetrics()}>
          <OperationCreateAction credits={getConversationMetrics().totalCredits} />
        </Show>
      </div>
    </div>
  );
};
